import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { getUserApi } from "../services/user.service";
import { getUnitApi } from "../services/unit.service";
import { getVisitTypeApi } from "../services/visitType.service";
import { addAppointmentApi } from "../services/appointment.service";
import { toast } from "react-toastify";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { getPatientByIdApi } from "../services/patient.service";
import {
  DatePicker,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import DiscountModal from "../components/appoinments/modal/discount-modal";
import AppointmentSchema from "../joi/appointment/appointment.schema";

const AppointmentsAdd = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [appointmentDate, setAppointmentDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [appointmentTime, setAppointmentTime] = useState(
    dayjs().format("HH:mm")
  );
  const [errors, setErrors] = useState([])

  const [patientData, setPatientData] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isErrorPatient, setIsErrorPatient] = useState(false);

  const getValidISODateTime = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}:00`;
    const parsed = new Date(combined);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const initialState = {
    UHID: "",
    patientId,
    unit: null,
    doctorName: null,
    visitType: null,
    appointmentDateTime: getValidISODateTime(appointmentDate, appointmentTime),
    amount: "",
    doctorNotes: "",
    discount: {
      discountReason: "",
      approvedBy: null,
      discountType: "Percentage",
      discountAmount: 0,
      discountPercentage: 0,
    },
    payment: {
      transactionId: null,
      transactionType: null,
      amountPaid: 0,
      remarks: "To Reception",
      paymentType: "",
    },
  };

  const [formData, setFormData] = useState(initialState);
  const [confirmAmountPaid, setConfirmAmountPaid] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isTransactionTypeSet, setIsTransactionTypeSet] = useState(false);

  const amountToBePaid =
    formData?.amount -
    (formData?.discount?.discountType === "Percentage"
      ? formData?.discount?.discountPercentage * formData?.amount * 0.01
      : formData?.discount?.discountAmount);

  // Updated validation logic to allow partial payments for "Pending Payment" transaction type
  const isPendingPayment = formData?.payment?.transactionType?.transactionMode === "Pending Payment";
  const amountsMatch = isPendingPayment 
    ? (Number(confirmAmountPaid) >= 0 && Number(confirmAmountPaid) <= Number(formData?.payment?.amountPaid))
    : formData?.payment?.amountPaid === Number(confirmAmountPaid);

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });

  const { dataList: visitTypeList } = useGetApi({
    apiFunction: getVisitTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "visitTypes",
  });

  const { dataList: transactionList } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });
  const navigation = useNavigate()
  const transactionTypeList = [
    ...(transactionList?.find((option) => option?.transactionMode === "Payment")?.transactionTypes || []),
    ...(transactionList?.find((option) => option?.transactionMode === "Pending Payment")?.transactionTypes || [])
  ];

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    if (
      name === "transactionType" ||
      name === "transactionId" ||
      name === "amountPaid" ||
      name === "remarks" ||
      name === "paymentType"
    ) {
      if (name === "transactionType" && parsedValue === null) {
        setFormData((preValue) => ({
          ...preValue,
          discount: {
            discountType: "Percentage",
            discountAmount: 0,
            discountPercentage: 0,
            discountReason: "",
            approvedBy: "",
          },
        }));
      }
      setFormData((preValue) => ({
        ...preValue,
        payment: {
          ...preValue?.payment,
          [name]: parsedValue,
        },
      }));
      return;
    }
    if (
      name === "discountReason" ||
      name === "approvedBy" ||
      name === "discountType" ||
      name === "discountAmount" ||
      name === "discountPercentage"
    ) {
      setFormData((preValue) => ({
        ...preValue,
        discount: {
          ...preValue?.discount,
          [name]: parsedValue,
        },
      }));
      return;
    }
    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleDoctorUnitChange = (e) => {
    const { name, value } = e.target;

    if (name === "doctorName") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        unit: value?.unit,
      }));
    }

    if (name === "unit") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        doctor: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      formData?.payment?.transactionType &&
      amountToBePaid !== formData?.payment?.amountPaid
    ) {
      toast.error("Amount and Amount to be paid doesn't match.");
      return;
    }

    if (!amountsMatch) {
      toast.error("Amount and Confirm Amount doesn't match.");
      return;
    }

    // Payment section validation
    if (!formData?.payment?.transactionType || !formData?.payment?.paymentType || formData?.payment?.amountPaid === '' || confirmAmountPaid === '') {
      toast.error("Please fill all payment details.");
      return;
    }

    const formDataWithId = {
      ...formData,
      visitType: formData?.visitType?._id,
      doctorName: formData?.doctorName?._id,
      unit: formData?.unit?._id,
      appointmentDateTime: getValidISODateTime(
        appointmentDate,
        appointmentTime
      ),
      payment: {
        ...formData?.payment,
        transactionType:
          typeof formData?.payment?.transactionType === "object"
            ? formData?.payment?.transactionType?._id
            : formData?.payment?.transactionType,
      },
    };
    
    const { error } = AppointmentSchema.validate(formDataWithId,  { abortEarly: false });

    if (error) {
      let errorName = [];
      error.details.forEach((err) => {
        errorName?.push(err?.context?.key)
      });
      setErrors(errorName);
      toast.error("Please fill the required fields")
      return;
    }

    setIsLoading(true);
    const response = await addAppointmentApi(formDataWithId);
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(response?.message || `Appointment added successfully`);
      navigate(`/patient/info/${patientId}`);
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    async function fetchPatient() {
      if (patientId && patientId !== 'undefined' && patientId !== 'null') {
        setIsLoadingPatient(true);
        setIsErrorPatient(false);
        try {
          const response = await getPatientByIdApi({ patientId });
          if (response?.code === 200 && response?.data) {
            setPatientData(response.data);
          } else {
            setPatientData(null);
            setIsErrorPatient(true);
          }
        } catch (err) {
          setPatientData(null);
          setIsErrorPatient(true);
        } finally {
          setIsLoadingPatient(false);
        }
      }
    }
    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    setFormData((preValue) => ({ ...preValue, UHID: patientData?.UHID }));
  }, [patientData]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {isLoadingPatient ? (
          <Loader />
        ) : isErrorPatient ? (
          <ErrorMessage />
        ) : (
          <Box
            component={"form"}
            onSubmit={handleSubmit}
            sx={{ width: "100%", pr: "80px", py: "20px" }}
          >
            {/* Form */}
            <Box
              sx={{
                padding: 2,
                borderRadius: 2,
              }}
            >
              <Button
                startIcon={<ArrowLeft />}
                onClick={() => navigate(-1)}
                sx={{
                  color: '#5D6A82', 
                  textTransform: 'none', 
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  marginBottom: '16px',
                }}
              >
                Back
              </Button>
              {/* Appointment Form */}
              <Typography
                sx={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '29px',
                  letterSpacing: '0px',
                  color: "#5D6A82",
                  marginBottom: "1rem",
                }}
              >
                Enter Appointment Details
              </Typography>

              {/* Patient Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={3} xl={2}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid #5d6a82",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                      boxSizing: "border-box",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {patientData?.patientImage ? (
                      <Box
                        component="img"
                        src={patientData.patientImage}
                        alt="Patient"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          fontSize: "14px",
                          textAlign: "center",
                        }}
                      >
                        No picture
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8} md={9} xl={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="grey">
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="UHID"
                        name="UHID"
                        value={patientData?.UHID || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Name"
                        name="name"
                        value={`${patientData?.firstName || ""} ${patientData?.lastName || ""}`}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Number"
                        name="phone"
                        value={patientData?.phone || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Gender"
                        name="gender"
                        value={patientData?.gender || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Address"
                        name="address"
                        value={patientData?.address || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="City"
                        name="city"
                        value={patientData?.city?.cityName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="State"
                        name="state"
                        value={patientData?.state?.stateName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Pincode"
                        name="pinCode"
                        value={patientData?.pinCode || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Appointment Information */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  my: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Appointment Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={unitList || []}
                      getOptionLabel={(option) => option?.unitName || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unit"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('unit') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }}
                        />
                      )}
                      value={formData?.unit || null}
                      onChange={(_, newValue) =>{
                        if (errors.includes("unit")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "unit"));
                        };
                        handleDoctorUnitChange({
                          target: { name: "unit", value: newValue },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={
                        formData?.unit
                          ? doctorsList.filter(
                              (d) =>
                                d?.role?.role === "Doctor" &&
                                d?.unit?._id === formData?.unit?._id
                            )
                          : doctorsList.filter(
                              (d) => d?.role?.role === "Doctor"
                            ) || []
                      }
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${
                          option?.lastName || ""
                        } (${option?.userId || ""})`
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Doctor Name"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                         />
                      )}
                      value={formData?.doctorName || null}
                      onChange={(_, newValue) =>{
                        handleDoctorUnitChange({
                          target: { name: "doctorName", value: newValue },
                        });
                        if (errors.includes("doctorName")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "doctorName"));
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={visitTypeList || []}
                      getOptionLabel={(option) =>
                        `${option?.name || ""} (${option?.visitType || ""})`
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Visit Type" 
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                        />
                      )}
                      value={formData?.visitType || null}
                      onChange={(_, newValue) =>{
                        handleInputChange({
                          target: { name: "visitType", value: newValue },
                        });
                        if (errors.includes("visitType")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "visitType"));
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Date"
                      disablePast
                      slotProps={{ textField: { fullWidth: true, required: true, InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } } } }}
                      value={appointmentDate ? dayjs(appointmentDate) : null}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('appointmentDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                      onChange={(newValue) =>
                        setAppointmentDate(
                          newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TimePicker
                      label="Time"
                      slotProps={{ textField: { fullWidth: true, required: true, InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } } } }}
                      value={
                        appointmentTime ? dayjs(appointmentTime, "HH:mm") : null
                      }
                      onChange={(newValue) =>
                        setAppointmentTime(
                          newValue ? dayjs(newValue).format("HH:mm") : null
                        )
                      }
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Amount"
                      name="amount"
                      value={formData?.amount === 0 ? '' : formData?.amount}
                      onChange={e => {
                        handleInputChange({ target: { name: 'amount', value: e.target.value === '' ? '' : Number(e.target.value) } });
                        if (errors.includes("amount")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "amount"));
                        }
                      }}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('amount') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Billing Information */}
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                {/* Overall Discount */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                    onClick={handleModalOpen}
                    disabled={!isTransactionTypeSet}
                  >
                    Overall Discount
                  </Button>

                  <DiscountModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    handleChange={handleInputChange}
                    formData={formData}
                    setFormData={setFormData}
                  />
                </Box>

                {/* Amount To Be Paid */}
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#EAEAEA",
                    borderRadius: 2,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Amount to be paid: ₹{amountToBePaid}
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>|</Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Net Expense: ₹{formData?.amount}
                  </Typography>
                </Box>

                {/* Transaction Details */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={transactionTypeList || []}
                      getOptionLabel={(option) => `${option?.name || ""} (${option?.paymentMethod})`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Transaction Type"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                        />
                      )}
                      value={formData?.payment?.transactionType || null}
                      onChange={(_, newValue) => {
                        if (errors.includes("transactionType")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "transactionType"));
                        }
                        setIsTransactionTypeSet(!!newValue);
                        handleInputChange({
                          target: {
                            name: "transactionType",
                            value: newValue,
                          },
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                      sx={{ minWidth: "100px" }}
                      required
                      fullWidth
                    >
                      <InputLabel
                        required
                        sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                      >
                        Payment Type
                      </InputLabel>
                      <Select
                        labelId="payment-type-id"
                        label="Payment Type"
                        name="paymentType"
                        value={formData?.payment?.paymentType || ""}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (errors.includes("paymentType")) {
                            setErrors((prevErrors) => prevErrors.filter((error) => error !== "paymentType"));
                          }
                        }}
                        required
                        sx={{
                          border: errors?.includes('paymentType') ? '1px solid red' : '1px solid inherit',
                        }}
                      >
                        <MenuItem value="InvoicePayment">Invoice Payment</MenuItem>
                        <MenuItem value="SelfPayment">Self Payment</MenuItem>
                        <MenuItem value="Refund">Refund</MenuItem>
                        <MenuItem value="IPDAdvance">IPD Advance</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {formData?.payment?.transactionType?.name ===
                    "QR Payment" ? (
                      <TextField
                        label="Transaction ID"
                        name="transactionId"
                        value={formData?.payment?.transactionId || ""}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (errors.includes("transactionId")) {
                            setErrors((prevErrors) => prevErrors.filter((error) => error !== "transactionId"));
                          }
                        }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('transactionId') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                          bgcolor: "white" 
                        }}
                      />
                    ) : (
                      <TextField
                        label="Transfer Bank A/C"
                        name="transferBankAccount"
                        value={
                          formData?.payment?.transactionType?.bankDetails?.find(
                            (bankDetail) => bankDetail?.default
                          )?.accountNumber || ""
                        }
                        slotProps={{ input: { readOnly: true } }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('transactionId') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}

                        fullWidth
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Amount (₹)"
                      name="amountPaid"
                      value={formData?.payment?.amountPaid === 0 ? '' : formData?.payment?.amountPaid}
                      onChange={e => {
                        handleInputChange({ target: { name: 'amountPaid', value: e.target.value === '' ? '' : Number(e.target.value) } });
                        if (errors.includes("amountPaid")) {
                          setErrors((prevErrors) => prevErrors.filter((error) => error !== "amountPaid"));
                        }
                      }}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('amountPaid') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Confirm Amount (₹)"
                      name="confirmAmountPaid"
                      value={confirmAmountPaid}
                      onChange={e => setConfirmAmountPaid(e.target.value === '' ? '' : Number(e.target.value))}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      error={!amountsMatch && confirmAmountPaid !== ""}
                      helperText={
                        !amountsMatch && confirmAmountPaid !== ""
                          ? isPendingPayment 
                            ? `Amount must be between ₹0 and ₹${formData?.payment?.amountPaid}`
                            : "Amount does not match!"
                          : ""
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: (errors?.includes('amountPaid') && !confirmAmountPaid) ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Remarks"
                      name="remarks"
                      value={formData?.payment?.remarks || ""}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Submit Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : `Submit`}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AppointmentsAdd;
